import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { TableFilters } from '~/client/src/desktop/components/TableFilters'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { useTableList } from '~/client/src/desktop/tables/useTableList'
import { CREATE_PROPERTY_RECORD_ID } from '~/client/src/desktop/views/ProjectSetUp/components/ProjectSetupResourceConfig/constants'
import PropertyEditSidebar from '~/client/src/desktop/views/ProjectSetUp/components/ProjectSetupResourceConfig/PropertyEditSidebar/PropertyEditSidebar'
import { Button } from '~/client/src/shared/components/Button/Button'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import * as Layout from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import { ViewOptionsPopover } from '~/client/src/shared/components/ViewOptions'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import ResourcePropertiesTable from './ResourcePropertiesTable'
import { buildResourcePropertyFields } from './resourcePropertyFields'
import { ResourcePropertiesColumnId } from './types'
import { useResourcePropertyRows } from './useResourcePropertyRows'

interface IProps {
  resourceTypeId: ResourceTypeId
  columnConfigKey: LocalStorageKey
  projectDateStore?: ProjectDateStore
  state?: DesktopInitialState
  resourceSchema: ResourceSchema
}

function ProjectSetupResourceProperties({
  resourceTypeId,
  columnConfigKey,
  projectDateStore,
  state,
  resourceSchema,
}: IProps): React.ReactElement | null {
  const projectId = state?.activeProject?.id
  const userId = state?.user?.id
  const [selectedRecordId, setSelectedRecordId] = React.useState<string | null>(
    null,
  )

  const properties = resourceSchema.allProperties
  const rows = useResourcePropertyRows(properties, projectDateStore)

  const rowActionHandlers = React.useMemo(
    () => ({ onEdit: setSelectedRecordId }),
    [],
  )
  const tableFields = React.useMemo(
    () => buildResourcePropertyFields({ rowActionHandlers }),
    [rowActionHandlers],
  )
  const tableList = useTableList({
    records: rows,
    schema: tableFields.schema,
    displayColumns: tableFields.columns,
    columnConfig: {
      options: tableFields.columnConfig,
      storageKey: columnConfigKey,
      projectId,
      userId,
    },
    initialQuery: {
      groupBy: [],
      sortKey: ResourcePropertiesColumnId.Label,
      sortOrder: SortOrder.ASC,
    },
    groupingAllowNone: true,
  })

  if (!projectId) return null

  if (resourceSchema.loading && rows.length === 0) {
    return (
      <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:items-center tw:justify-center">
        <Loader />
      </section>
    )
  }

  const closeSidebar = () => setSelectedRecordId(null)

  return (
    <>
      <Layout.View className="tw:relative tw:overflow-hidden">
        <Layout.Header>
          <div className="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-3">
            <SearchInput {...tableList.searchProps} />
            <TableFilters {...tableList.filterProps} />
            <div className="tw:flex-1" />
            <ViewOptionsPopover {...tableList.viewOptionsProps} />
            <Button
              variant="default"
              onClick={() => setSelectedRecordId(CREATE_PROPERTY_RECORD_ID)}
            >
              {Localization.translator.createNew}
            </Button>
          </div>
        </Layout.Header>
        <Layout.Content>
          <ResourcePropertiesTable
            list={tableList}
            onEdit={setSelectedRecordId}
          />
        </Layout.Content>
      </Layout.View>

      {selectedRecordId && (
        <PropertyEditSidebar
          resourceTypeId={resourceTypeId}
          selectedRecordId={selectedRecordId}
          properties={properties}
          onClose={closeSidebar}
          resourceSchema={resourceSchema}
        />
      )}
    </>
  )
}

export default inject(
  'projectDateStore',
  'state',
)(observer(ProjectSetupResourceProperties))
